import React, { FC, useCallback, useEffect, useMemo, useState } from "react";

import { faQuestionCircle } from "@fortawesome/pro-solid-svg-icons/faQuestionCircle";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { defineMessages, useIntl } from "react-intl";

import { isDefined } from "@mapmycustomers/shared/util/assert";
import { Modal } from "@mapmycustomers/ui";

import RadioGroup from "@app/component/input/RadioGroup";
import { useExtendedAnalytics } from "@app/util/analytic/AnalyticsService";
import useAnalytics from "@app/util/contexts/useAnalytics";

import DeleteReason, { allDeleteReasons, getDeleteReasonDisplayName } from "../enum/DeleteReason";

import styles from "./DeleteConfirmation.module.scss";

const messages = defineMessages({
  description: {
    id: "reports.mileageTracking.deleteReasonModal.description",
    defaultMessage: "Please select an option below for why you’d like to delete this drive.",
    description: "Title of the delete modal in mileage tracking",
  },
  okButton: {
    id: "reports.mileageTracking.deleteReasonModal.ok",
    defaultMessage: "Delete{multiple, select, true { ({count})} other {}}",
    description: "Title of the delete modal in mileage tracking",
  },
  title: {
    id: "reports.mileageTracking.deleteReasonModal",
    defaultMessage: "Are you sure?",
    description: "Description of the delete modal in mileage tracking",
  },
});

export interface DeleteConfirmationProps {
  count: number;
  onCancel: () => void;
  onConfirm?: (deleteReason: DeleteReason) => void;
}

const DeleteConfirmation: FC<DeleteConfirmationProps> = ({ count, onCancel, onConfirm }) => {
  const analytics = useExtendedAnalytics("Delete Drives", useAnalytics());
  useEffect(() => {
    analytics.initiated("");
  }, [analytics]);

  const intl = useIntl();
  const [deleteReason, setDeleteReason] = useState<DeleteReason | undefined>();

  const handleOk = useCallback(() => {
    if (isDefined(deleteReason) && onConfirm) {
      analytics.completed("", {
        deleteReason: getDeleteReasonDisplayName(intl, deleteReason, count > 1),
      });
      onConfirm(deleteReason);
    }
  }, [analytics, onConfirm, deleteReason, intl, count]);

  const handleCancel = useCallback(() => {
    analytics.cancelled("");
    onCancel();
  }, [analytics, onCancel]);

  const options = useMemo(
    () =>
      allDeleteReasons.map((reason) => ({
        label: getDeleteReasonDisplayName(intl, reason, count > 1),
        value: reason,
      })),
    [count, intl]
  );

  return (
    <Modal
      okButtonProps={{ danger: true, disabled: !isDefined(deleteReason) }}
      okText={intl.formatMessage(messages.okButton, {
        count,
        multiple: count > 1,
      })}
      onCancel={handleCancel}
      onOk={handleOk}
      open
      title={
        <div className={styles.title}>
          <FontAwesomeIcon className={styles.icon} icon={faQuestionCircle} />
          {intl.formatMessage(messages.title)}
        </div>
      }
    >
      <div className={styles.content}>
        <div>{intl.formatMessage(messages.description)}</div>
        <RadioGroup<DeleteReason>
          onChange={setDeleteReason}
          options={options}
          value={deleteReason}
          vertical
        />
      </div>
    </Modal>
  );
};

export default DeleteConfirmation;
